export function stopMediaStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((track) => track.stop());
}

export function cameraApiSupported(): boolean {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
}

/** Inicia preview priorizando a câmera traseira. */
export async function startRearCamera(): Promise<MediaStream> {
  if (!cameraApiSupported()) {
    throw new Error("Câmera não suportada neste navegador.");
  }
  const attempts: MediaStreamConstraints[] = [
    { video: { facingMode: { ideal: "environment" } }, audio: false },
    { video: { facingMode: "environment" }, audio: false },
    { video: true, audio: false },
  ];
  let lastError: unknown;
  for (const constraints of attempts) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError instanceof Error ? lastError : new Error("Não foi possível acessar a câmera.");
}

/** Liga o stream ao <video> com atributos exigidos pelo Safari/iPhone. */
export async function attachStreamToVideo(video: HTMLVideoElement, stream: MediaStream): Promise<void> {
  video.setAttribute("playsinline", "true");
  video.setAttribute("webkit-playsinline", "true");
  video.setAttribute("x-webkit-airplay", "deny");
  video.removeAttribute("controls");
  video.removeAttribute("poster");
  video.controls = false;
  video.muted = true;
  video.defaultMuted = true;
  video.autoplay = true;
  video.playsInline = true;
  video.disablePictureInPicture = true;
  if ("disableRemotePlayback" in video) {
    video.disableRemotePlayback = true;
  }
  video.srcObject = stream;
  if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
    await new Promise<void>((resolve, reject) => {
      const onReady = () => {
        video.removeEventListener("loadedmetadata", onReady);
        video.removeEventListener("error", onError);
        resolve();
      };
      const onError = () => {
        video.removeEventListener("loadedmetadata", onReady);
        video.removeEventListener("error", onError);
        reject(new Error("Não foi possível iniciar o preview da câmera."));
      };
      video.addEventListener("loadedmetadata", onReady);
      video.addEventListener("error", onError);
    });
  }
  await video.play();
  if (!video.paused && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("canplay", onCanPlay);
      video.removeEventListener("error", onError);
    };
    const onPlaying = () => {
      cleanup();
      resolve();
    };
    const onCanPlay = () => {
      if (!video.paused) {
        cleanup();
        resolve();
      }
    };
    const onError = () => {
      cleanup();
      reject(new Error("Não foi possível iniciar o preview da câmera."));
    };
    video.addEventListener("playing", onPlaying);
    video.addEventListener("canplay", onCanPlay);
    video.addEventListener("error", onError);
  });
}

/** Detecta câmera em celular/tablet. Desktop (mesmo com webcam) retorna false. */
export async function deviceHasCamera(): Promise<boolean> {
  if (!cameraApiSupported()) {
    return false;
  }

  const ua = navigator.userAgent || "";
  const isMobileLike = /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua);
  const isIpadOs =
    typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    (navigator.maxTouchPoints ?? 0) > 1;

  if (!isMobileLike && !isIpadOs) {
    return false;
  }

  try {
    if (typeof navigator.mediaDevices.enumerateDevices === "function") {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (devices.some((d) => d.kind === "videoinput")) {
        return true;
      }
    }
  } catch {
    /* tenta getUserMedia */
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: "environment" } },
      audio: false,
    });
    stopMediaStream(stream);
    return true;
  } catch {
    return false;
  }
}
